import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getWebOrdersByCustomer } from '../apis/backendRequests'
import { makeRequest } from '../apis/makeRequest'
import { cartActions } from '../actions/cartActions'
import {
    Table,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableBody,
    IconButton,
    Button,
    TextField,
    Box,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import PopUpOrder from './PopUpOrder'
import { GetDetailOrder } from '../helpers/GetDetailsOrder'
import {
    ORDER_PRODUCT,
    ORDER_SORTABLE_COLUMNS,
    getDisplayOrderId,
    formatOrderFechaDisplay,
    getStatusLabel,
    OrderProductSortHeaderIcon,
    OrderHeadCellSort,
    OrderStyledTableCell,
    useOrderProductList,
} from '../helpers/orderProductTable'
import { notificationActions, orderActions, spinnerActions } from '../actions'
import { useNavigate } from 'react-router-dom'

export default function OrderProduct() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { dynamicsId } = useSelector((s) => s.account)
    const { items } = useSelector((s) => s.cart)
    const [webOrder, setWebOrder] = useState([])
    const [rowPopUp, setRowPopUp] = useState({ item: {}, open: false })

    const {
        orderSearch,
        setOrderSearch,
        userSearch,
        setUserSearch,
        dateFilter,
        setDateFilter,
        statusFilter,
        setStatusFilter,
        sortState,
        cycleSort,
        sortHeaderIsActive,
        totalFiltered,
        visibleRows,
        visibleCount,
        scrollContainerRef,
        loadMoreSentinelRef,
    } = useOrderProductList(webOrder)

    useEffect(() => {
        if (webOrder.length > 0) return
        makeRequest(
            () => getWebOrdersByCustomer(dynamicsId, ORDER_PRODUCT.apiStatusAll),
            (res) => {
                if (res.data.length) setWebOrder(res.data)
            },
            null,
            { checkEmptyResults: false }
        )
    }, [webOrder, dynamicsId])

    const viewOrder = async (item) => {
        dispatch(spinnerActions.show())
        try {
            dispatch(orderActions.clear())
            dispatch(orderActions.setOrderId(item.webOrderId))
            if (item.integrationId && item.integrationId !== 'XXX') {
                dispatch(orderActions.setIntegrationId(item.integrationId))
            }
            const result = await GetDetailOrder(dynamicsId, item.webOrderId, dispatch, orderActions)
            if (result?.success === false) {
                dispatch(notificationActions.showError({ message: 'Error al obtener algunos productos del pedido' }))
            }
            setRowPopUp({ item, open: true })
        } finally {
            dispatch(spinnerActions.hide())
        }
    }

    const editOrder = async (item) => {
        dispatch(spinnerActions.show())
        try {
            dispatch(cartActions.clear())
            dispatch(cartActions.setOrderId(item.webOrderId))
            if (item.integrationId && item.integrationId !== 'XXX') {
                dispatch(cartActions.setIntegrationId(item.integrationId))
            }
            const result = await GetDetailOrder(dynamicsId, item.webOrderId, dispatch, cartActions, true)
            if (result?.success === false) {
                dispatch(notificationActions.showError({ message: 'Error al obtener algunos productos del pedido' }))
            }
            navigate('/cart', { state: items })
        } finally {
            dispatch(spinnerActions.hide())
        }
    }

    return (
        <>
            <Box sx={ORDER_PRODUCT.filtersBoxSx}>
                <TextField
                    size="small"
                    label="Orden"
                    placeholder="Nº o integración"
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    variant="outlined"
                    inputProps={{ 'aria-label': 'Filtrar por número de orden' }}
                    sx={ORDER_PRODUCT.filterFieldSx({ maxWidth: 150 })}
                />
                <TextField
                    size="small"
                    label="Usuario"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    variant="outlined"
                    inputProps={{ 'aria-label': 'Filtrar por usuario' }}
                    sx={ORDER_PRODUCT.filterFieldSx({ maxWidth: 160 })}
                />
                <TextField
                    size="small"
                    type="date"
                    label="Fecha"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'Filtrar por fecha' }}
                    sx={ORDER_PRODUCT.filterFieldSx({ minWidth: 150, maxWidth: 170 })}
                />
                <FormControl size="small" sx={ORDER_PRODUCT.filterFieldSx({ minWidth: 150, maxWidth: 180 })}>
                    <InputLabel id="order-status-filter">Estado</InputLabel>
                    <Select
                        labelId="order-status-filter"
                        label="Estado"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value={1}>Sin integrar</MenuItem>
                        <MenuItem value={2}>Integrado</MenuItem>
                        <MenuItem value={3}>Borrador</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer ref={scrollContainerRef} component={Paper} elevation={1} sx={ORDER_PRODUCT.tableContainerSx}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {ORDER_SORTABLE_COLUMNS.map((col) => (
                                <OrderHeadCellSort
                                    key={col.key}
                                    align="center"
                                    onClick={() => cycleSort(col.key)}
                                    active={sortHeaderIsActive(col.key)}
                                    sx={{ cursor: 'pointer', userSelect: 'none', ...col.headSx }}
                                >
                                    <Box
                                        component="span"
                                        sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        {col.label}
                                        <OrderProductSortHeaderIcon columnKey={col.key} sortState={sortState} />
                                    </Box>
                                </OrderHeadCellSort>
                            ))}
                            <OrderStyledTableCell align="center">Editar</OrderStyledTableCell>
                            <OrderStyledTableCell align="center">Ver detalle</OrderStyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length > 0 ? (
                            visibleRows.map((item) => (
                                <TableRow
                                    key={item.webOrderId}
                                    hover
                                    sx={{ '&:nth-of-type(even) .MuiTableCell-body': { backgroundColor: '#ededed' } }}
                                >
                                    <OrderStyledTableCell align="center" sx={{ whiteSpace: 'nowrap', maxWidth: 120 }}>
                                        <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                            {getDisplayOrderId(item)}
                                        </Typography>
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell
                                        align="left"
                                        sx={{ maxWidth: 120, lineHeight: 1.4, wordBreak: 'break-word' }}
                                    >
                                        {item.appUserId}
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                        {formatOrderFechaDisplay(item)}
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell
                                        align="left"
                                        sx={{
                                            pl: 2.5,
                                            maxWidth: 260,
                                            lineHeight: 1.5,
                                            verticalAlign: 'top',
                                            whiteSpace: 'pre-line',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        <Typography component="div" variant="body2" sx={{ color: 'text.primary' }}>
                                            {item.notes ?? '—'}
                                        </Typography>
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell align="center" sx={{ lineHeight: 1.4 }}>
                                        {getStatusLabel(item.status)}
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        ${Number(item.total).toLocaleString()}
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell align="center">
                                        <Button
                                            onClick={() => editOrder(item)}
                                            disabled={item.status !== 3}
                                            size="small"
                                            variant="outlined"
                                        >
                                            {item.status === 3 ? 'Editar' : 'No editable'}
                                        </Button>
                                    </OrderStyledTableCell>
                                    <OrderStyledTableCell align="center">
                                        <IconButton onClick={() => viewOrder(item)} size="small" color="primary">
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </OrderStyledTableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <OrderStyledTableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary" variant="body2">
                                        {webOrder.length === 0 ? 'Cargando pedidos…' : 'No hay pedidos con los filtros actuales.'}
                                    </Typography>
                                </OrderStyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {totalFiltered > 0 && visibleCount < totalFiltered && (
                    <Box ref={loadMoreSentinelRef} aria-hidden sx={{ height: 12, width: '100%' }} />
                )}
            </TableContainer>
            {totalFiltered > 0 && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, ml: 0.5 }}>
                    Mostrando {visibleRows.length} de {totalFiltered}
                    {visibleCount < totalFiltered ? ' — deslizá hacia abajo para cargar más' : null}
                </Typography>
            )}
            <PopUpOrder row={rowPopUp} setter={setRowPopUp} />
        </>
    )
}
